import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, HostBinding, Inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, ActivatedRoute } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppDataService, MenuService } from 'app/services';
import { AppComponent } from 'app/app.component';
import { MenuPermissionActionEnum, MenuPermissionConfig } from 'app/models';
import { MenuLabel } from 'app/layout/menuitem/menu-label';
import { navigationMenuTestId } from 'app/layout/menuitem/navigation-menu-testid';
import { ProvideTestId, TEST_ID_TOKEN } from 'app/providers';

@Component({
    /* tslint:disable:component-selector */
    selector: '[app-menuitem]',
    providers:[ProvideTestId('subitem')],
    /* tslint:enable:component-selector */
    templateUrl: './menuitem.component.html',
    styleUrls: ['./menuitem.component.scss'],
    animations: [
        trigger('children', [
            state('void', style({
                height: '0px'
            })),
            state('hiddenAnimated', style({
                height: '0px'
            })),
            state('visibleAnimated', style({
                height: '*'
            })),
            state('visible', style({
                height: '*',
                'z-index': 100
            })),
            state('hidden', style({
                height: '0px',
                'z-index': '*'
            })),
            state('slimVisibleAnimated', style({
                opacity: 1,
                transform: 'none'
            })),
            state('slimHiddenAnimated', style({
                opacity: 0,
                transform: 'translateX(20px)'
            })),
            transition('visibleAnimated => hiddenAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
            transition('hiddenAnimated => visibleAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
            transition('void => visibleAnimated, visibleAnimated => void',
                animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
            transition('void => slimVisibleAnimated', animate('400ms cubic-bezier(.05,.74,.2,.99)')),
            transition('slimHiddenAnimated => slimVisibleAnimated', animate('400ms cubic-bezier(.05,.74,.2,.99)'))
        ])
    ]
})
export class MenuitemComponent implements OnInit, OnDestroy {
    @Input() item: any;

    @Input() index: number = 0;

    @Input() root: boolean = false;

    @Input() parentKey: string = '';

    @HostBinding('class.active-menuitem')
    active = false;

    menuSourceSubscription: Subscription;
    menuResetSubscription: Subscription;

    public menuPermissionConfig = MenuPermissionConfig;
    public menuPermissionActionEnum = MenuPermissionActionEnum;
    public readonly navigationMenuTestId = navigationMenuTestId;

    key: string = '';
    private isRouteBasedActivation: boolean = false;

    constructor(
        public app: AppComponent,
        public appData: AppDataService,
        public router: Router,
        private activatedRoute: ActivatedRoute,
        private cd: ChangeDetectorRef,
        private menuService: MenuService,
        @Inject(TEST_ID_TOKEN) public componentTestId: string) {
        this.menuSourceSubscription = this.menuService.menuSource$.subscribe(key => {
            // Use original logic for click-based activation
            if (!this.isRouteBasedActivation) {
                if (this.active && this.key !== key && key.indexOf(this.key) !== 0) {
                    this.active = false;
                }
            } else {
                if (this.active && this.key !== key && !this.isChildOf(key)) {
                    this.active = false;
                } else if (this.key === key) {
                    this.active = true;
                }
            }
        });

        this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
            this.active = false;
        });

        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe((params: any) => {
				this.isRouteBasedActivation = true;
				requestAnimationFrame(() => {
					this.updateActiveStateFromCurrentRoute(params.url);
					if (this.item.items && this.item.items.length > 0) {
						this.checkChildItemsActiveState(params.url);
					}
					// Reset flag after ensuring all updates are complete
					setTimeout(() => {
						this.isRouteBasedActivation = false;
					}, 50);
				});
            });
    }

    ngOnInit() {
        this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);
        if (!(this.app.isSlim() || this.app.isHorizontal())) {
            requestAnimationFrame(() => {
                this.initializeActiveState().catch(error => {
                    console.error('Error initializing menu item active state:', error);
                });
            });
        }
    }

    private async initializeActiveState() {
        const currentUrl = this.router.url;
		this.isRouteBasedActivation = true;

        try {
            await this.performActiveStateCheck(currentUrl);
            await this.waitForRouterStability();
            await this.performActiveStateCheck(this.router.url);
        } finally {
            setTimeout(() => {
                this.isRouteBasedActivation = false;
            }, 100);
        }
    }

    private async performActiveStateCheck(currentUrl: string) {
        if (!currentUrl || currentUrl === '/') {
            return;
        }
        this.updateActiveStateFromCurrentRoute(currentUrl);
        if (this.item.items && this.item.items.length > 0) {
            this.checkChildItemsActiveState(currentUrl);
        }

        if (this.item.routerLink && this.item.routerLink.length > 0) {
            const isActive = this.router.isActive(this.item.routerLink[0], false);
            if (isActive) {
                this.active = true;
                if (this.parentKey) {
                    this.activateParentMenu();
                }
            }
        }

        this.checkActiveRoute();
    }

    private async waitForRouterStability(): Promise<void> {
        return new Promise<void>((resolve) => {
            // Wait for the next tick to ensure router state is stable
            setTimeout(resolve, 0);
        });
    }

    private checkActiveRoute() {
        if (this.item.routerLink && this.item.routerLink.length > 0) {
            const isActive = this.router.isActive(this.item.routerLink[0], false);
            if (isActive) {
                this.active = true;
                if (this.parentKey) {
                    this.activateParentMenu();
                }
            }
        }
    }

    updateActiveStateFromRoute() {
        this.active = this.router.isActive(this.item.routerLink[0], !this.item.items && !this.item.preventExact);
    }

    updateActiveStateFromCurrentRoute(currentUrl: string) {
        const wasActive = this.active;
        if (this.item.route && this.item.route.length > 0) {
			const itemRoute = this.item.route[0];
            if (currentUrl === itemRoute || currentUrl.startsWith(itemRoute + '/')) {
                this.active = true;
                if (!wasActive && this.parentKey) {
                    this.activateParentMenu();
                }
                return;
            }
        }

        if (this.item.routerLink && this.item.routerLink.length > 0) {
            const itemRouterLink = this.item.routerLink[0];
            if (currentUrl === itemRouterLink || currentUrl.startsWith(itemRouterLink + '/')) {
                this.active = true;
                if (!wasActive && this.parentKey) {
                    this.activateParentMenu();
                }
                return;
            }
        }
        this.active = false;
    }

    private activateParentMenu() {
        if (!this.isRouteBasedActivation) {
            this.isRouteBasedActivation = true;
            const parentKeys = this.getAllParentKeys();

            for (const parentKey of parentKeys) {
                this.menuService.onMenuStateChange(parentKey);
            }

            setTimeout(() => {
                this.isRouteBasedActivation = false;
            }, 50);
        }
    }

    private getAllParentKeys(): string[] {
        const parentKeys: string[] = [];

        if (!this.parentKey) {
            return parentKeys;
        }

        parentKeys.push(this.parentKey);

        const keyParts = this.parentKey.split('-');

        for (let i = keyParts.length - 1; i > 0; i--) {
            const ancestorKey = keyParts.slice(0, i).join('-');
            parentKeys.push(ancestorKey);
        }

        return parentKeys;
    }

    private isChildOf(parentKey: string): boolean {
        return this.parentKey === parentKey || this.key.startsWith(parentKey + '-');
    }

    private checkChildItemsActiveState(currentUrl: string) {
        if (this.item.items && this.item.items.length > 0) {
            for (const childItem of this.item.items) {
                if (childItem.route && childItem.route.length > 0) {
                    const childRoute = childItem.route[0];
                    if (currentUrl === childRoute || currentUrl.startsWith(childRoute + '/')) {
                        this.active = true;
                        return;
                    }
                }

                if (childItem.routerLink && childItem.routerLink.length > 0) {
                    const childRouterLink = childItem.routerLink[0];
                    if (currentUrl === childRouterLink || currentUrl.startsWith(childRouterLink + '/')) {
                        this.active = true;
                        return;
                    }
                }

                if (childItem.items && childItem.items.length > 0) {
                    if (this.checkNestedChildren(childItem.items, currentUrl)) {
                        this.active = true;
                        return;
                    }
                }
            }
        }
    }

    private checkNestedChildren(items: any[], currentUrl: string): boolean {
        for (const item of items) {
            if (item.route && item.route.length > 0) {
                const itemRoute = item.route[0];
                if (currentUrl === itemRoute || currentUrl.startsWith(itemRoute + '/')) {
                    return true;
                }
            }

            if (item.routerLink && item.routerLink.length > 0) {
                const itemRouterLink = item.routerLink[0];
                if (currentUrl === itemRouterLink || currentUrl.startsWith(itemRouterLink + '/')) {
                    return true;
                }
            }

            if (item.items && item.items.length > 0) {
                if (this.checkNestedChildren(item.items, currentUrl)) {
                    return true;
                }
            }
        }
        return false;
    }

    itemClick(item: any, event: Event) {
        // avoid processing disabled items
        if (this.item.disabled) {
            event.preventDefault();
            return;
        }

        // navigate with hover in horizontal mode
        if (this.root) {
            this.app.menuHoverActive = !this.app.menuHoverActive;
        }

        // notify other items
        this.menuService.onMenuStateChange(this.key);

        // execute command
        if (this.item.command) {
            this.item.command({ originalEvent: event, item: this.item });
        }

        // toggle active state
        if (this.item.items) {
            this.active = !this.active;
        } else {
            // activate item
            this.active = true;

            // reset horizontal menu
            if (this.app.isSlim() || this.app.isHorizontal()) {
                this.menuService.reset();
            }

            this.app.staticMenuMobileActive = false;
            this.app.menuHoverActive = !this.app.menuHoverActive;

            this.app.unblockBodyScroll();
        }

         if (item.menuFlag) {
            sessionStorage.setItem('currentMenuItem', JSON.stringify(item));
            this.app.goToMenuRoute(item);
        }
    }

    onMouseEnter() {
        // activate item on hover
        if (this.root && this.app.menuHoverActive &&
            (this.app.isSlim() || this.app.isHorizontal()) && this.app.isDesktop()) {
            this.menuService.onMenuStateChange(this.key);
            this.active = true;
        }
    }

    ngOnDestroy() {
        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }
    }

    public containIcon(iconName: string) {
        const words = iconName.split(' ');

        for (const word of words) {
            const lowercasedWord = word.toLowerCase();

            if (lowercasedWord.includes('pi')) {
                return true;
            }
        }

        return false;
    }

    public isIconExist(icon: string) {
        if (icon && icon.trim() !== '') {
            return true;
        }
        return false;
    }

    public getIconName(icon: string) {
        if (this.isIconExist(icon) && this.active) {
            const tokens = icon.split('.');
            return tokens[0]+'-active.'+tokens[1];
        } else {
            return icon;
        }
    }

    public getPaddingForSubItem(item: any) {
        if (!item.icon) {
            switch (item.label) {
                case MenuLabel.Bookings:
                case MenuLabel.Reports:
                case MenuLabel.Drivers:
                case MenuLabel.Merchants:
                case MenuLabel.Dashboard:
                case MenuLabel.BroadcastMessage:
                case MenuLabel.OfflineMarketing:
                case MenuLabel.Events:
                case MenuLabel.XpressAppMarketing:
                case MenuLabel.SystemStatus:
                case MenuLabel.UserManagement:
                case MenuLabel.Security:
                case MenuLabel.VehicleAndFares:
                case MenuLabel.Coding:
                case MenuLabel.Accounting:
                case MenuLabel.Operators:
                case MenuLabel.Transactions:
                case MenuLabel.Maps:
                case MenuLabel.Users: return '32px';
                case MenuLabel.Eats:
                case MenuLabel.Mart:
                case MenuLabel.Promotions:
                case MenuLabel.Driver:
                case MenuLabel.CampaignRiders:
                case MenuLabel.Advertisements: return '42px';
                case MenuLabel.Disciplinary: return this.appData.isBoracayLocation ? '42px' : '32px';
                default: return undefined;
            }
        } else {
            return '0px';
        }
    }
}
